/**
 * UC3 launch updates (client, 8 Oct 2026): what the UC3 stepper puts on the map at each
 * step, as pure functions so the mapping is testable without a map.
 *
 * * `rasterForStep` — the one raster a step switches on and the legend title it carries:
 *   the crop's area (`mask_<crop>`, or `gaez_prd_<crop>` for a crop with no mask) on the
 *   Crop and Productivity steps, swapped for the modelled yield or the yield gap when the
 *   reader picks that measure on Productivity; NDVI, rainfall (or AETI) and the soil layer
 *   on their own steps. Every one of them is clipped to the isolated geography by
 *   `IntelIsolate`, which is why nothing here names a clip.
 * * `yieldTerciles` — the survey yield of every ranked watershed, split into thirds by rank
 *   (low, medium, high). The productivity map paints the watersheds inside the selection
 *   with these classes; the analysis and the results never read them.
 */
import type { IntelUc3Crop } from '../../lib/api';

/** `#uc3Measure`: presentation only, the diagnosis always uses the survey yield. */
export const UC3_MEASURES = ['survey', 'modelled', 'gap'] as const;
export type Uc3Measure = (typeof UC3_MEASURES)[number];

export type Tercile = 'low' | 'medium' | 'high';
export const TERCILES: readonly Tercile[] = ['low', 'medium', 'high'];
/**
 * Low to high yield, amber to green: a different hue family from the crop-area raster's
 * greens underneath, so the classes read over it.
 */
export const TERCILE_COLORS: Record<Tercile, string> = {
  low: '#D9822B',
  medium: '#E9D37A',
  high: '#4E9A3F',
};

export interface Uc3RasterChoice {
  layerId: string;
  /** i18n key under `intel.uc3.layer.*` for the legend title. */
  titleKey: string;
}

export interface Uc3RasterInputs {
  crop: Pick<IntelUc3Crop, 'id' | 'mask' | 'gaez'> | undefined;
  measure: Uc3Measure;
  ndviLayerId: string;
  rainfallLayerId: string;
  aetiOn: boolean;
  soilLayer: string;
}

/** The crop's own area layer: its mask, or its 2020 production where it has none. */
export function cropAreaRaster(crop: Uc3RasterInputs['crop']): Uc3RasterChoice | null {
  if (!crop) return null;
  if (crop.mask) return { layerId: crop.mask, titleKey: 'intel.uc3.layer.mask' };
  return { layerId: `gaez_prd_${crop.id}`, titleKey: 'intel.uc3.layer.production' };
}

/** The raster a step id switches on, or `null` for a step that draws none. */
export function rasterForStep(stepId: string, inputs: Uc3RasterInputs): Uc3RasterChoice | null {
  switch (stepId) {
    case 'crop':
      return cropAreaRaster(inputs.crop);
    case 'indicator':
      if (!inputs.crop) return null;
      if (inputs.measure === 'modelled') {
        return { layerId: inputs.crop.gaez ?? `gaez_yld_${inputs.crop.id}`, titleKey: 'intel.uc3.layer.gaez' };
      }
      if (inputs.measure === 'gap') {
        return { layerId: `gaez_gap_${inputs.crop.id}`, titleKey: 'intel.uc3.layer.gaez_gap' };
      }
      return cropAreaRaster(inputs.crop);
    case 'ndvi':
      return { layerId: inputs.ndviLayerId, titleKey: '' };
    case 'rainfall':
      return { layerId: inputs.aetiOn ? 'aeti' : inputs.rainfallLayerId, titleKey: '' };
    case 'soil':
      return { layerId: inputs.soilLayer, titleKey: `intel.uc3.layer.${inputs.soilLayer}` };
    default:
      return null;
  }
}

/**
 * Rank thirds of the survey yield across every ranked watershed: the lowest third `low`,
 * the highest third `high`. Ties keep their rank order; a unit with no yield is left out.
 */
export function yieldTerciles(rows: readonly { unit_id: string; yield?: number | null }[]): Map<string, Tercile> {
  const valued = rows
    .filter((r): r is { unit_id: string; yield: number } => typeof r.yield === 'number' && Number.isFinite(r.yield))
    .sort((a, b) => a.yield - b.yield);
  const out = new Map<string, Tercile>();
  const n = valued.length;
  valued.forEach((row, i) => {
    out.set(row.unit_id, TERCILES[Math.min(2, Math.floor((i * 3) / n))]!);
  });
  return out;
}
